import { AutocompleteRenderOptionState, Theme } from '@mui/material';
import { ChangeEvent, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Tag } from '@synergy/models';
import { makeStyles } from '@mui/styles';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { ChipAutocomplete } from '../../autocomplete/chip-autocomplete';
import { useFilter } from '../context';

const useStyles = makeStyles((theme: Theme) => ({
    highlight: {
        color: theme.palette.primary.main,
    },
}));

interface TagsFilterProps {
    tags: Tag[];
    disabled: boolean;
    fetchTags?: () => void;
}

export const TagsFilter = (props: TagsFilterProps) => {
    const { updateFilter, registerFilterMap, filter } = useFilter();
    const classes = useStyles();

    useEffect(() => {
        props.fetchTags?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleValueChange = (_event: ChangeEvent<unknown>, tags: Tag[]) => {
        const tagIds = tags.map(tag => tag.id);
        filter.delete('tagIds');
        tagIds.forEach(id => filter.append('tagIds', id.toString()));
        updateFilter(filter);
    };

    const getValues = () => {
        const currentFilterTags = filter.getAll('tagIds').map(id => +id);
        if (currentFilterTags.length > 0) {
            return props.tags.filter(tag => currentFilterTags.includes(tag.id));
        }

        return [];
    };

    const renderOption = (tag: Tag, state: AutocompleteRenderOptionState) => {
        const nameMatches = match(tag.name, state.inputValue);
        const nameParts = parse(tag.name, nameMatches);

        return (
            <div>
                {nameParts.map(namePart => (
                    <span className={namePart.highlight ? classes.highlight : ''}>{namePart.text}</span>
                ))}
            </div>
        );
    };

    registerFilterMap('tagIds', (tagId: unknown) => props.tags.find(tag => tag.id === Number(tagId))?.name);

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <ChipAutocomplete
                value={getValues()}
                noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                options={props.tags}
                renderOption={renderOption}
                getOptionLabel={(tag: Tag) => tag.name}
                getChipLabel={(tag: Tag) => tag.name}
                textfieldPlaceholder="app.filter.demandTags"
                onChange={handleValueChange}
                textfieldMaxWidth={424}
                disabled={props.disabled}
            />
        </div>
    );
};
